// /settings/return-policy — Return / Credit Note authorisation policy.
//
// Owner-only screen. Sets the minimum tenant role level that can issue a
// credit note or process a return. Affects:
//   • Return mode in POS billing
//   • "Initiate return" button on Bill Detail (POS + admin)
//   • POST /bills/:id/return (backend gate)
//
// One number on the wire (10 | 30 | 50). Cached for 60s in Redis on the
// API side; this page invalidates that cache on save so changes apply to
// the next request, not after the TTL.
//
// Also sets the high-value approval threshold (FR-BILL-026): returns
// refunding more than this amount are parked for manager approval instead
// of being processed at the till. Stored in minor units; 0 = off.
//
// Category rules (FR-BILL-053): each product category can override the
// return window and the approval threshold. Blank = inherit the store
// default; window 0 = non-returnable. Saved per category via PATCH
// /categories/:id — the API applies the category rule per bill line.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Skeleton,
  Alert,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import { amountToMajor, getCurrencyDecimals, getSubunitMultiplier, majorToAmount } from '@bbpos/utils';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { tenantService } from '@/services/tenant.service';
import { categoryService } from '@/services/category.service';
import type { Category } from '@/types/category';
import { ApiError } from '@/lib/api-client';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { SaveStatus } from '@/components/SaveStatus';

type RoleFloor = 10 | 30 | 50;

/** Editable text for one category's overrides; '' means "inherit the store default". */
type CategoryRuleDraft = { window: string; threshold: string };

const OPTIONS: { value: RoleFloor; title: string; subtitle: string }[] = [
  {
    value: 50,
    title: 'Owner only',
    subtitle: 'Strictest. Cashiers and managers cannot issue credit notes.',
  },
  {
    value: 30,
    title: 'Manager and above',
    subtitle: 'Recommended. Matches the default. Cashiers cannot.',
  },
  {
    value: 10,
    title: 'Any cashier',
    subtitle: 'Most permissive. Every cashier can issue credit notes.',
  },
];

export function ReturnPolicyPage() {
  const profile = useAuthStore((s) => s.profile);
  const refreshProfile = useAuthStore((s) => s.fetchProfile);
  const { toast } = useToast();

  const tenantId = profile?.tenant?.id ?? null;
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const decimals = getCurrencyDecimals(currency);
  // Minor units → plain major-unit string (no grouping) to seed the input.
  const toMajorInput = (minor: number): string =>
    (minor / getSubunitMultiplier(currency)).toFixed(decimals);

  const [current, setCurrent] = useState<RoleFloor | null>(null);
  const [draft, setDraft] = useState<RoleFloor | null>(null);
  // Approval threshold in minor units (0 = off) and its editable major-unit text.
  const [currentThreshold, setCurrentThreshold] = useState<number>(0);
  const [thresholdInput, setThresholdInput] = useState<string>('0');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // FR-BILL-053 — per-category overrides.
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryDrafts, setCategoryDrafts] = useState<Record<string, CategoryRuleDraft>>({});
  const [savingCategoryId, setSavingCategoryId] = useState<string | null>(null);

  const draftFor = (c: Category): CategoryRuleDraft => ({
    window: c.return_window_days === null || c.return_window_days === undefined
      ? ''
      : String(c.return_window_days),
    threshold: c.return_approval_threshold === null || c.return_approval_threshold === undefined
      ? ''
      : toMajorInput(Number(c.return_approval_threshold)),
  });

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    categoryService
      .list({ is_active: true })
      .then((res) => {
        if (cancelled) return;
        const list = res.data ?? [];
        setCategories(list);
        setCategoryDrafts(Object.fromEntries(list.map((c) => [c.id, draftFor(c)])));
      })
      .catch(() => {
        // The category table is optional context; the main policy still works.
        if (!cancelled) setCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    tenantService
      .getReturnPolicy(tenantId)
      .then((res) => {
        if (cancelled) return;
        const lvl = (res.min_role_level as RoleFloor) ?? 30;
        setCurrent(lvl);
        setDraft(lvl);
        const threshold = Number(res.approval_threshold ?? 0);
        setCurrentThreshold(threshold);
        setThresholdInput(toMajorInput(threshold));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load policy');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const parsedThreshold = Number(thresholdInput);
  const thresholdValid = thresholdInput.trim() !== '' && Number.isFinite(parsedThreshold) && parsedThreshold >= 0;
  const draftThreshold = thresholdValid ? majorToAmount(parsedThreshold, currency) : currentThreshold;
  const roleDirty = draft !== null && draft !== current;
  const thresholdDirty = thresholdValid && draftThreshold !== currentThreshold;
  const canSave = !saving && draft !== null && thresholdValid && (roleDirty || thresholdDirty);
  // A blank or invalid threshold is an unsaved edit too, so the page never
  // reads "All changes saved" while one is in the field.
  const formDirty = draft !== null && (roleDirty || thresholdDirty || !thresholdValid);
  const { dialog: leaveDialog } = useUnsavedChangesGuard(formDirty);

  const handleSave = async () => {
    if (!tenantId || !canSave || draft === null) return;
    setSaving(true);
    try {
      await tenantService.setReturnPolicy(tenantId, {
        ...(roleDirty ? { min_role_level: draft } : {}),
        ...(thresholdDirty ? { approval_threshold: draftThreshold } : {}),
      });
      setCurrent(draft);
      setCurrentThreshold(draftThreshold);
      setThresholdInput(toMajorInput(draftThreshold));
      toast({ title: 'Return policy updated', variant: 'success' });
      // Re-fetch the auth profile so `permissions.can_issue_credit_note`
      // updates immediately — affects whether the user themselves can use
      // returns next time they hit POS.
      await refreshProfile();
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to save',
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const parseWindow = (text: string): number | null | undefined => {
    if (text.trim() === '') return null;
    const n = Number(text);
    return Number.isInteger(n) && n >= 0 ? n : undefined;
  };
  const parseThreshold = (text: string): number | null | undefined => {
    if (text.trim() === '') return null;
    const n = Number(text);
    return Number.isFinite(n) && n >= 0 ? majorToAmount(n, currency) : undefined;
  };
  const categoryDraftValid = (d: CategoryRuleDraft) =>
    parseWindow(d.window) !== undefined && parseThreshold(d.threshold) !== undefined;
  const categoryDirty = (c: Category) => {
    const d = categoryDrafts[c.id];
    if (!d) return false;
    const saved = draftFor(c);
    return d.window !== saved.window || d.threshold !== saved.threshold;
  };

  const handleSaveCategory = async (c: Category) => {
    const d = categoryDrafts[c.id];
    if (!d || !categoryDraftValid(d) || savingCategoryId) return;
    const window = parseWindow(d.window) as number | null;
    const threshold = parseThreshold(d.threshold) as number | null;
    setSavingCategoryId(c.id);
    try {
      await categoryService.update(c.id, {
        return_window_days: window,
        return_approval_threshold: threshold,
      });
      setCategories((prev) =>
        prev.map((x) =>
          x.id === c.id
            ? { ...x, return_window_days: window, return_approval_threshold: threshold }
            : x,
        ),
      );
      toast({ title: `Return rules saved for ${c.name}`, variant: 'success' });
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to save category rules',
        variant: 'error',
      });
    } finally {
      setSavingCategoryId(null);
    }
  };

  return (
    <PageContainer
      title="Return policy"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'Return policy' },
      ]}
    >
      <ErrorBoundary resetKey="return-policy">
        <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-lg mb-6 animate-fade-in">
          <h1 className="text-2xl font-bold text-white">Who can issue credit notes?</h1>
          <p className="mt-1.5 text-sm text-white/85 max-w-2xl">
            Controls the Return mode in POS and the &ldquo;Initiate return&rdquo; action on bills.
            Lower the threshold to delegate routine returns to your floor staff;
            keep it high if every credit note needs your sign-off.
          </p>
        </div>

        {error && (
          <div className="mb-5">
            <Alert
              variant="danger"
              title="Couldn't load the current policy"
              description={error}
            />
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rect" height={72} />
            ))}
          </div>
        ) : (
          <div className={cn(GLASS_FORM, 'p-5 space-y-3')}>
            {OPTIONS.map((opt) => {
              const checked = draft === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDraft(opt.value)}
                  className={cn(
                    'w-full text-left rounded-lg border p-4 transition-all duration-200',
                    checked
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20 ring-2 ring-brand-200 dark:ring-brand-800'
                      : 'border-neutral-200 dark:border-neutral-700 hover:border-brand-300 hover:bg-brand-50/40 dark:hover:bg-neutral-700/40',
                  )}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-900 dark:text-white">
                        {opt.title}
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                        {opt.subtitle}
                      </p>
                    </div>
                    <span
                      className={cn(
                        'w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0',
                        checked
                          ? 'border-brand-500'
                          : 'border-neutral-300 dark:border-neutral-500',
                      )}
                    >
                      {checked && <span className="w-2.5 h-2.5 rounded-full bg-brand-500" />}
                    </span>
                  </div>
                </button>
              );
            })}

            <div className="pt-3 mt-3 border-t border-neutral-200 dark:border-neutral-700">
              <h2 className="font-semibold text-neutral-900 dark:text-white">
                High-value returns need a manager
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 mb-3">
                Returns refunding more than this amount are held for manager or owner approval
                and the managers are notified. Leave it at 0 to process every return at the till.
              </p>
              <div className="max-w-xs">
                <Input
                  label={`Approval threshold (${symbol})`}
                  type="number"
                  min={0}
                  step={1 / getSubunitMultiplier(currency)}
                  value={thresholdInput}
                  onChange={(v: string) => setThresholdInput(v)}
                  error={thresholdValid ? undefined : 'Enter an amount of 0 or more'}
                  helperText={
                    currentThreshold > 0
                      ? `Currently ${symbol}${amountToMajor(currentThreshold, currency)}`
                      : 'Currently off'
                  }
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <SaveStatus dirty={formDirty} />
              <Link to="/settings" className="text-sm text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300">
                Cancel
              </Link>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={!canSave}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}

        {!loading && (
          <section className={cn(GLASS_FORM, 'p-5 mt-6 animate-slide-up')} aria-label="Category return rules">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-lg font-bold">
                ↺
              </div>
              <h2 className="font-semibold text-neutral-900 dark:text-white">
                Category rules
              </h2>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
              A category rule wins over the store default for items in that category. Leave a
              field blank to use the store default. A return window of 0 days makes the
              category non-returnable; an approval threshold of 0 needs no approval.
            </p>
            {categories.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                No active categories yet. Create categories under Products to set rules here.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-500 dark:text-neutral-400">
                      <th className="py-2 pr-3 font-medium">Category</th>
                      <th className="py-2 pr-3 font-medium">Return window (days)</th>
                      <th className="py-2 pr-3 font-medium">Approval threshold ({symbol})</th>
                      <th className="py-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c) => {
                      const d = categoryDrafts[c.id] ?? { window: '', threshold: '' };
                      const valid = categoryDraftValid(d);
                      const dirty = categoryDirty(c);
                      const hasRule =
                        (c.return_window_days ?? null) !== null ||
                        (c.return_approval_threshold ?? null) !== null;
                      return (
                        <tr
                          key={c.id}
                          className="border-t border-neutral-100 dark:border-neutral-700 even:bg-neutral-50 dark:even:bg-neutral-800/40 hover:bg-brand-50 dark:hover:bg-neutral-700/40 transition-all duration-200"
                        >
                          <td className="py-2 pr-3 align-top">
                            <span className="font-medium text-neutral-900 dark:text-white">{c.name}</span>
                            <span
                              className={cn(
                                'ml-2 inline-block rounded-full px-2 py-0.5 text-xs',
                                hasRule
                                  ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                                  : 'bg-neutral-100 text-neutral-500 dark:bg-neutral-700 dark:text-neutral-300',
                              )}
                            >
                              {hasRule ? 'Custom' : 'Default'}
                            </span>
                          </td>
                          <td className="py-2 pr-3 align-top">
                            <Input
                              aria-label={`${c.name} return window (days)`}
                              type="number"
                              min={0}
                              step={1}
                              placeholder="Default"
                              value={d.window}
                              onChange={(v: string) =>
                                setCategoryDrafts((prev) => ({ ...prev, [c.id]: { ...d, window: v } }))
                              }
                              error={parseWindow(d.window) === undefined ? 'Whole days, 0 or more' : undefined}
                            />
                          </td>
                          <td className="py-2 pr-3 align-top">
                            <Input
                              aria-label={`${c.name} approval threshold (${symbol})`}
                              type="number"
                              min={0}
                              step={1 / getSubunitMultiplier(currency)}
                              placeholder="Default"
                              value={d.threshold}
                              onChange={(v: string) =>
                                setCategoryDrafts((prev) => ({ ...prev, [c.id]: { ...d, threshold: v } }))
                              }
                              error={parseThreshold(d.threshold) === undefined ? 'Amount of 0 or more' : undefined}
                            />
                          </td>
                          <td className="py-2 align-top text-right">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleSaveCategory(c)}
                              disabled={!valid || !dirty || savingCategoryId !== null}
                            >
                              {savingCategoryId === c.id ? 'Saving…' : 'Save'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </ErrorBoundary>
      {leaveDialog}
    </PageContainer>
  );
}
